/**
 * «Долги» — просрочено, не проведены, долг по поставщикам и поиск
 * накладной по номеру (§11.1 ТЗ `ops`). Списки строит сервер теми же
 * функциями, что бот: `payments.overdue`, `unposted`, `by_supplier`, `find`.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { rub } from '../../api';
import { Icon } from '../../icons';
import { Link, navigate, useRoute } from '../../router';
import { Dot, isoDate } from '../../ui';
import { LIVE, paymentsApi } from './api';
import type { Debts as DebtsData, InvoiceList, ListKind, PayInvoice } from './api';
import { cardPath } from './paths';

interface ListState {
  kind: ListKind;
  supplierId?: number;
  q?: string;
}

function readList(params: URLSearchParams): ListState {
  const kind = params.get('list');
  const supplier = Number(params.get('supplier_id'));
  if (kind === 'supplier' && supplier) return { kind, supplierId: supplier };
  if (kind === 'search' && (params.get('q') ?? '').trim().length >= 2) {
    return { kind, q: params.get('q')!.trim() };
  }
  if (kind === 'unposted') return { kind };
  return { kind: 'overdue' };
}

function listPath(state: ListState): string {
  const params = new URLSearchParams({ list: state.kind });
  if (state.supplierId) params.set('supplier_id', String(state.supplierId));
  if (state.q) params.set('q', state.q);
  return `/payments/debts?${params.toString()}`;
}

export function Debts() {
  const route = useRoute();
  const list = readList(route.params);
  const [search, setSearch] = useState(list.q ?? '');

  const debts = useQuery<DebtsData, ApiError>({
    queryKey: ['payments', 'debts'],
    queryFn: () => paymentsApi.debts(),
    ...LIVE,
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = search.trim();
    if (q.length >= 2) navigate(listPath({ kind: 'search', q }));
  };

  if (debts.isPending) return <Loading />;
  if (debts.error) return <ErrorState error={debts.error} onRetry={() => debts.refetch()} />;
  const data = debts.data;

  return (
    <div className="pay-stack">
      <div className="pay-tiles">
        <button
          type="button"
          className={`pay-tile${list.kind === 'overdue' ? ' pay-tile--active' : ''}`}
          onClick={() => navigate(listPath({ kind: 'overdue' }))}
        >
          <span className="pay-tile__label">Просрочено</span>
          <span className="pay-tile__value">{rub(data.overdue.total)}</span>
          <span className="faint">{data.overdue.count} накл.</span>
        </button>
        <div className="pay-tile pay-tile--static">
          <span className="pay-tile__label">Весь долг</span>
          <span className="pay-tile__value">{rub(data.debt.total)}</span>
          <span className="faint">{data.debt.count} накл.</span>
        </div>
        <button
          type="button"
          className={`pay-tile${list.kind === 'unposted' ? ' pay-tile--active' : ''}`}
          onClick={() => navigate(listPath({ kind: 'unposted' }))}
        >
          <span className="pay-tile__label">Не проведены</span>
          <span className="pay-tile__value">{rub(data.unposted.total)}</span>
          <span className="faint">в долг не входят</span>
        </button>
      </div>

      <form className="search pay-search" onSubmit={submit} role="search">
        <Icon name="search" size={16} />
        <input
          className="search__input"
          type="search"
          placeholder="Номер накладной — можно часть"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label="Поиск накладной по номеру"
        />
        <button type="submit" className="button button--small" disabled={search.trim().length < 2}>
          Найти
        </button>
      </form>

      <div className="pay-debts">
        <section className="pay-card pay-suppliers" aria-label="Долг по поставщикам">
          <h2 className="block-title">По поставщикам</h2>
          {data.suppliers.length === 0 ? (
            <p className="faint">Долгов нет ни у одного поставщика.</p>
          ) : (
            <ul className="pay-list">
              {data.suppliers.map((supplier) => (
                <li key={supplier.id}>
                  <button
                    type="button"
                    className={`pay-row${
                      list.kind === 'supplier' && list.supplierId === supplier.id
                        ? ' pay-row--active'
                        : ''
                    }`}
                    onClick={() => navigate(listPath({ kind: 'supplier', supplierId: supplier.id }))}
                  >
                    <span className="pay-row__main">
                      <span className="pay-row__title">{supplier.name}</span>
                      <span className="faint">
                        {supplier.count} накл.
                        {supplier.overdue > 0 && `, просрочено ${supplier.overdue}`}
                      </span>
                    </span>
                    <span className="num">{rub(supplier.total)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
        <InvoiceListView list={list} />
      </div>
    </div>
  );
}

const EMPTY: Record<ListKind, string> = {
  overdue: 'Просроченных накладных нет. Это и есть целевое значение.',
  unposted: 'Непроведённых накладных нет.',
  supplier: 'Долга перед этим поставщиком нет.',
  search: 'Ничего не нашлось. Попробуйте другой номер.',
};

function InvoiceListView({ list }: { list: ListState }) {
  const query = useQuery<InvoiceList, ApiError>({
    queryKey: ['payments', 'invoices', list.kind, list.supplierId ?? null, list.q ?? null],
    queryFn: () => paymentsApi.invoices(list.kind, { supplier_id: list.supplierId, q: list.q }),
    ...LIVE,
    placeholderData: (previous) => previous,
  });

  return (
    <section className="pay-card pay-invoices" aria-label="Накладные">
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
          <header className="pay-card__head">
            <h2 className="pay-card__title">{query.data.title}</h2>
            <span className="pay-card__total">
              {query.data.count} накл. · {rub(query.data.total)}
            </span>
          </header>
          {query.data.invoices.length === 0 ? (
            <Empty title="Пусто" text={EMPTY[list.kind]} />
          ) : (
            <ul className="pay-list">
              {query.data.invoices.map((invoice) => (
                <InvoiceRow key={invoice.id} invoice={invoice} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

export function InvoiceRow({ invoice }: { invoice: PayInvoice }) {
  const when = !invoice.is_posted
    ? 'не проведена'
    : invoice.is_settled
      ? invoice.status
      : invoice.overdue_days > 0
        ? `просрочено ${invoice.overdue_days} дн.`
        : `срок ${isoDate(invoice.due_date)}`;
  return (
    <li>
      <Link to={cardPath(invoice.id)} className="pay-row">
        <span className="pay-row__main">
          <span className="pay-row__title">
            <Dot colour={invoice.overdue_days > 0 && !invoice.is_settled ? 'red' : null} />{' '}
            {invoice.supplier.name}
          </span>
          <span className="faint">
            накл. {invoice.external_number ?? invoice.number}{' '}
            <strong className="order__date">от {isoDate(invoice.doc_date)}</strong> · {when}
            {invoice.backdated ? ' · занесена задним числом' : ''}
          </span>
        </span>
        <span className="num">{rub(invoice.balance)}</span>
      </Link>
    </li>
  );
}
